import React from 'react';
import KeyboardReact, { KeyboardReactInterface } from 'react-simple-keyboard';

import { BUTTON_LABELS } from '../../utils/KeyUtils';
import { sidebarLayout, actionLayout } from './layouts/sidebar';

import './japanese.css';
import { useIntl } from 'react-intl';
import japaneseModule from './modules';
import candidatesModule from 'components/Keyboard/modules/candidates';
import getJapaneseCandidates from 'components/Keyboard/Custom/Japanese/layouts/provider';

import type { KeyboardComponentProps } from '../types';
import { SPECIAL_KEYS } from 'components/Keyboard/utils/constants';
import { KeyboardLayoutType } from 'components/Keyboard/utils/KeyUtils';

const BUTTON_THEME = [
  {
    class: 'keyboard-divider',
    buttons: SPECIAL_KEYS.DIVIDER,
  },
  {
    class: 'col-span-2',
    buttons: '{kaomoji} {zenkaku} {shift} {bksp}',
  },
  {
    class: 'col-span-4',
    buttons: '{shift}',
  },
  {
    class: '!w-auto !max-w-none',
    buttons: '@',
  },
];

const JapaneseKeyboard: React.FC<KeyboardComponentProps> = ({
  type,
  language,
  layoutType,
  layoutCandidates,
  onInit,
  onChange,
  keyboardRef,
  onKeyReleased,
  onSelectionChange,
}) => {
  const intl = useIntl();
  const display = React.useMemo(() => BUTTON_LABELS(intl), [intl]);

  const actionDisplay = React.useMemo(() => ({
    ...BUTTON_LABELS(intl),
    // Override display for special keys for Japanese keyboard
    [SPECIAL_KEYS.SPACE]: '空白',
  }), [intl]);

  const isAlternateLayout = layoutType === KeyboardLayoutType.ALTERNATE;

  const initialLayout = isAlternateLayout ? SPECIAL_KEYS.ABC : SPECIAL_KEYS.KANA;

  const sidebarRef = React.useRef<KeyboardReactInterface>(null);
  const [activeScreen, setActiveScreen] = React.useState<string>(initialLayout);

  const setSidebarRef = React.useCallback((ref: KeyboardReactInterface) => {
    sidebarRef.current = ref;
  }, []);

  const mainKeyboardRef = React.useRef<KeyboardReactInterface>(null);
  const actionRef = React.useRef<KeyboardReactInterface>(null);
  const [hasCandidateSelected, setHasCandidateSelected] = React.useState(false);

  const setMainRef = React.useCallback(
    (ref: KeyboardReactInterface) => {
      mainKeyboardRef.current = ref;
      keyboardRef(ref);
      (ref as any).onCandidatesChange = (hasCandidates: boolean) => {
        setHasCandidateSelected(false);
        actionRef.current?.setOptions({
          layoutName: hasCandidates ? 'withCandidates' : 'default',
        });
      };
    },
    [keyboardRef],
  );

  const setActionRef = React.useCallback((ref: KeyboardReactInterface) => {
    actionRef.current = ref;
  }, []);

  const handlePanelKeyPress = React.useCallback((button: string) => {
    if ([SPECIAL_KEYS.NUMBERS, SPECIAL_KEYS.ABC, SPECIAL_KEYS.KANA].includes(button as SPECIAL_KEYS)) {
      setActiveScreen(button);
    }

    if (button === SPECIAL_KEYS.NEXT_CANDIDATE) {
      const selected = (mainKeyboardRef.current as any)?.selectNextCandidate?.();
      if (selected) {
        setHasCandidateSelected(true);
      }
      return;
    }

    if (button === SPECIAL_KEYS.ENTER) {
      if (hasCandidateSelected) {
        (mainKeyboardRef.current as any)?.confirmSelectedCandidate?.();
        setHasCandidateSelected(false);
      }
      // {enter} is disabled when no candidate is selected
      return;
    }

    mainKeyboardRef.current?.handleButtonClicked?.(button);
  }, [hasCandidateSelected]);


  const actionButtonThemes = React.useMemo(
    () => hasCandidateSelected ? [] : [{ class: 'disabled', buttons: SPECIAL_KEYS.ENTER }],
    [hasCandidateSelected],
  );

  const modules = React.useMemo(
    () => [candidatesModule(getJapaneseCandidates), japaneseModule(initialLayout)],
    [initialLayout],
  );

  const sidebarButtonThemes = React.useMemo(
    () => [
      {
        class: '!bg-gray-300',
        buttons: activeScreen,
      },
    ],
    [activeScreen],
  );

  return (
    <div className="flex gap-4">
      <KeyboardReact
        mergeDisplay
        display={display}
        layout={sidebarLayout}
        preventMouseDownDefault
        keyboardRef={setSidebarRef}
        onKeyPress={handlePanelKeyPress}
        buttonTheme={sidebarButtonThemes}
        theme="hg-theme-default fb-keyboard japanese-left"
      />
      <KeyboardReact
        type={type}
        mergeDisplay
        newLineOnEnter
        onInit={onInit}
        display={display}
        onChange={onChange}
        language={language}
        layoutType={layoutType}
        keyboardRef={setMainRef}
        buttonTheme={BUTTON_THEME}
        onKeyReleased={onKeyReleased}
        layoutCandidatesPageSize={5}
        layoutCandidatesCaseSensitiveMatch
        layoutCandidates={layoutCandidates}
        onSelectionChange={onSelectionChange}
        modules={modules}
        theme={`hg-theme-default fb-keyboard paginated-candidates japanese-middle ${activeScreen.match('^{(.*)}$')?.at(1) ?? ''}`}
      />
      <KeyboardReact
        mergeDisplay
        display={actionDisplay}
        layout={actionLayout}
        preventMouseDownDefault
        keyboardRef={setActionRef}
        onKeyPress={handlePanelKeyPress}
        buttonTheme={actionButtonThemes}
        theme="hg-theme-default fb-keyboard japanese-right"
      />
    </div>
  );
};

export default JapaneseKeyboard;
